import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate, courseImageUrl, type Course, type CourseEdition } from "@/lib/api";
import PublicNavbar from "@/components/feature/PublicNavbar";
import PublicFooter from "@/components/feature/PublicFooter";
import PageMeta from "@/components/feature/PageMeta";
import { useBranding } from "@/contexts/BrandingContext";

export default function RegistroPage() {
  const branding = useBranding();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [formData, setFormData] = useState({
    full_name: searchParams.get("nombre") || "", 
    id_number: "", 
    email: searchParams.get("email") || "", 
    phone: searchParams.get("telefono") || "", 
    company: "", 
    position: "",
    edition_id: "", emergency_contact: "", emergency_phone: "",
    payment_option: "none" as "none" | "partial" | "full",
    payment_amount: "", payment_method: "transferencia", payment_reference: "",
  });
  const [proof, setProof] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.getPublicCourses(), api.getPublicDiplomados()])
      .then(([courseData, diplomadoData]) => {
        const diplomadoAsCourses = diplomadoData.map((d) => ({ ...d, editions: d.editions?.map((ed) => ({
          id: ed.id,
          course_id: d.id,
          edition_label: ed.edition_label,
          start_date: ed.start_date,
          end_date: ed.end_date,
          location: ed.location,
          instructor_id: ed.instructor_id,
          evaluator_id: ed.evaluator_id,
          capacity: ed.capacity,
          price: ed.price,
          status: ed.status,
          service_type: "diplomado" as const,
        })) })) as Course[];
        setCourses([...courseData, ...diplomadoAsCourses]);
      })
      .catch(() => setCourses([]));
  }, []);

  const editions = useMemo(() => {
    const list: (CourseEdition & { courseName: string })[] = [];
    courses.forEach((c) => {
      c.editions?.forEach((ed) => list.push({ ...ed, courseName: c.name }));
    });
    return list.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  }, [courses]);

  const selectedEdition = editions.find((e) => e.id === formData.edition_id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (k !== "edition_id") fd.append(k, String(v));
      });
      fd.append(selectedEdition?.service_type === "diplomado" ? "diplomado_edition_id" : "edition_id", formData.edition_id);
      if (proof) fd.append("proof", proof);
      const result = await api.publicEnrollment(fd);
      setIsNewUser(!!result.is_new_user);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al enviar");
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
        <PublicNavbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="card max-w-lg text-center py-12 px-8">
            <i className="ri-check-line text-4xl text-green-400"></i>
            <h2 className="text-xl font-bold mt-4">Registro enviado</h2>
            <p className="text-sm text-[var(--color-text-secondary)] mt-2">
              {isNewUser
                ? "Revise su correo para confirmación y credenciales de acceso al portal."
                : "Su inscripción fue registrada. Le contactaremos cuando sea aprobada."}
            </p>
            <Link to="/" className="btn-primary mt-6 inline-block">Inicio</Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
      <PageMeta title={`Registro | ${branding.site_name}`} description="Inscríbase a cursos de capacitación industrial KSM Services." path="/registro" />
      <PublicNavbar />
      <div className="flex-1 px-4 py-12 pt-[96px] max-w-3xl mx-auto w-full">
        <h1 className="text-3xl font-bold text-center mb-8">Registro de capacitación</h1>
        {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="card space-y-4">
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-2 text-yellow-500 text-sm flex items-start gap-3">
            <i className="ri-error-warning-line text-lg mt-0.5"></i>
            <div>
              <p className="font-bold mb-0.5">Aviso Importante</p>
              <p>Por favor, utilice el <strong>nombre real y completo</strong> del estudiante, así como sus datos personales exactos. Esta información se utilizará para emitir el certificado oficial al finalizar la capacitación.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input className="input-field" placeholder="Nombre completo *" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required />
            <input className="input-field" placeholder="Cédula / ID *" value={formData.id_number} onChange={(e) => setFormData({ ...formData, id_number: e.target.value })} required />
            <input type="email" className="input-field" placeholder="Email *" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
            <input className="input-field" placeholder="Teléfono *" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
            <input className="input-field" placeholder="Empresa" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
            <input className="input-field" placeholder="Cargo" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm mb-2">Edición del curso o diplomado *</label>
            <select className="input-field" value={formData.edition_id} onChange={(e) => setFormData({ ...formData, edition_id: e.target.value })} required>
              <option value="">Seleccione curso y fecha...</option>
              {editions.map((ed) => (
                <option key={ed.id} value={ed.id}>
                  {ed.courseName} — {ed.edition_label || formatDate(ed.start_date)} ({ed.location})
                </option>
              ))}
            </select>
          </div>
          {selectedEdition && (
            <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-4 text-sm">
              <p><strong>Precio:</strong> ${selectedEdition.price.toLocaleString("en-US")} USD</p>
              <p><strong>Fechas:</strong> {formatDate(selectedEdition.start_date)} — {formatDate(selectedEdition.end_date)}</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <input className="input-field" placeholder="Contacto emergencia" value={formData.emergency_contact} onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })} />
            <input className="input-field" placeholder="Tel. emergencia" value={formData.emergency_phone} onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm mb-2">Pago</label>
            <select className="input-field" value={formData.payment_option} onChange={(e) => setFormData({ ...formData, payment_option: e.target.value as typeof formData.payment_option })}>
              <option value="none">Sin pago</option>
              <option value="partial">Abono parcial</option>
              <option value="full">Pago completo</option>
            </select>
          </div>
          {formData.payment_option !== "none" && (
            <div className="grid md:grid-cols-3 gap-4">
              <input type="number" className="input-field" placeholder="Monto" value={formData.payment_amount} onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })} required />
              <input className="input-field" placeholder="Referencia" value={formData.payment_reference} onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })} />
              <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} className="input-field" />
            </div>
          )}
          <button type="submit" disabled={submitting} className="btn-primary w-full cursor-pointer">{submitting ? "Enviando..." : "Enviar registro"}</button>
        </form>
      </div>
      <PublicFooter />
    </div>
  );
}
